import { useMemo } from "react";
import { Background, Controls, Handle, Position, ReactFlow } from "@xyflow/react";
import type { Edge, Node, NodeProps } from "@xyflow/react";
import { useQuery } from "@tanstack/react-query";
import { Card, Group, Loader, Stack, Text } from "@mantine/core";
import { IconGitBranch, IconHome } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { RunSummary } from "../api/types";

import "@xyflow/react/dist/style.css";

interface DecisionTreePanelProps {
  /** Любой узел дерева — запрос поднимется до корня и вернёт всё. */
  currentRunId: string;
}

interface RunNodeData {
  run: RunSummary;
  isCurrent: boolean;
  onClick: (runId: string) => void;
  [key: string]: unknown;
}

function RunNode({ data }: NodeProps) {
  const d = data as RunNodeData;
  const date = new Date(d.run.created_at).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const isRoot = !d.run.parent_id;

  return (
    <Card
      withBorder
      padding="xs"
      shadow={d.isCurrent ? "md" : "none"}
      style={{
        width: 220,
        cursor: "pointer",
        borderColor: d.isCurrent
          ? "var(--mantine-color-indigo-6)"
          : "var(--mantine-color-gray-3)",
        borderWidth: d.isCurrent ? 2 : 1,
      }}
      onClick={() => d.onClick(d.run.id)}
    >
      <Handle type="target" position={Position.Top} style={{ visibility: "hidden" }} />
      <Stack gap={2}>
        <Group gap={4} wrap="nowrap">
          {isRoot ? <IconHome size={12} /> : <IconGitBranch size={12} />}
          <Text size="xs" c="dimmed">
            {date}
          </Text>
        </Group>
        <Text size="xs" lineClamp={3} fw={d.isCurrent ? 600 : 400}>
          {d.run.task_preview}
        </Text>
      </Stack>
      <Handle type="source" position={Position.Bottom} style={{ visibility: "hidden" }} />
    </Card>
  );
}

const nodeTypes = { run: RunNode };

/**
 * Простая лэйаут-функция: укладывает дерево по уровням сверху вниз.
 * Корень — уровень 0; каждый child на уровень глубже. Дети одного родителя
 * расходятся горизонтально.
 */
function layoutTree(runs: RunSummary[]): { nodes: Node[]; edges: Edge[] } {
  const childrenOf: Record<string, RunSummary[]> = {};
  let root: RunSummary | undefined;

  for (const r of runs) {
    if (!r.parent_id) {
      root = r;
    } else {
      childrenOf[r.parent_id] = childrenOf[r.parent_id] ?? [];
      childrenOf[r.parent_id].push(r);
    }
  }

  if (!root) {
    // Дерево без явного корня (data integrity issue) — берём самый ранний
    root = [...runs].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    )[0];
  }

  const NODE_W = 220;
  const NODE_H = 100;
  const X_GAP = 40;
  const Y_GAP = 40;

  // Рекурсивно считаем ширину поддерева (в количестве листьев)
  const subtreeWidth = (id: string): number => {
    const ch = childrenOf[id] ?? [];
    if (ch.length === 0) return 1;
    return ch.reduce((sum, c) => sum + subtreeWidth(c.id), 0);
  };

  const positions: Record<string, { x: number; y: number }> = {};

  const place = (id: string, depth: number, leftCenter: number) => {
    const w = subtreeWidth(id) * (NODE_W + X_GAP);
    positions[id] = {
      x: leftCenter + w / 2 - NODE_W / 2,
      y: depth * (NODE_H + Y_GAP),
    };
    let cursor = leftCenter;
    for (const child of childrenOf[id] ?? []) {
      const childW = subtreeWidth(child.id) * (NODE_W + X_GAP);
      place(child.id, depth + 1, cursor);
      cursor += childW;
    }
  };

  if (root) place(root.id, 0, 0);

  const nodes: Node[] = runs.map((r) => ({
    id: r.id,
    type: "run",
    position: positions[r.id] ?? { x: 0, y: 0 },
    data: { run: r } as RunNodeData,
    draggable: false,
  }));

  const edges: Edge[] = runs
    .filter((r) => r.parent_id)
    .map((r) => ({
      id: `${r.parent_id}->${r.id}`,
      source: r.parent_id!,
      target: r.id,
      type: "smoothstep",
      animated: false,
    }));

  return { nodes, edges };
}

export function DecisionTreePanel({ currentRunId }: DecisionTreePanelProps) {
  const navigate = useNavigate();

  const { data: runs, isLoading } = useQuery({
    queryKey: ["tree", currentRunId],
    queryFn: () => api.getTree(currentRunId),
    enabled: Boolean(currentRunId),
  });

  const onNodeClick = (runId: string) => {
    if (runId !== currentRunId) navigate(`/runs/${runId}`);
  };

  const { nodes, edges } = useMemo(() => {
    if (!runs || runs.length === 0) return { nodes: [], edges: [] };
    const layout = layoutTree(runs);
    // Прокинем onClick и isCurrent в data
    layout.nodes = layout.nodes.map((n) => ({
      ...n,
      data: {
        ...(n.data as RunNodeData),
        isCurrent: n.id === currentRunId,
        onClick: onNodeClick,
      },
    }));
    return layout;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs, currentRunId]);

  if (isLoading) {
    return (
      <div style={{ height: "100%", minHeight: 400 }}>
        <Text fw={600} size="sm" mb="xs">
          🌳 Дерево решений
        </Text>
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      </div>
    );
  }

  // Один узел — placeholder с подсказкой про кнопку ветвления.
  if (!runs || runs.length <= 1) {
    return (
      <div style={{ height: "100%", minHeight: 400 }}>
        <Text fw={600} size="sm" mb="xs">
          🌳 Дерево решений
        </Text>
        <Card
          withBorder
          padding="lg"
          radius="md"
          style={{
            borderStyle: "dashed",
            background: "var(--mantine-color-gray-0)",
          }}
        >
          <Stack gap="xs" align="flex-start">
            <Group gap="xs">
              <IconGitBranch size={20} color="var(--mantine-color-gray-6)" />
              <Text fw={600}>Это корневой прогон</Text>
            </Group>
            <Text size="sm" c="dimmed">
              Дерево начнёт строиться, когда ты создашь ветку: нажми{" "}
              <Text component="span" fw={600}>
                «Ветвь / уточнить»
              </Text>{" "}
              сверху, чтобы пересчитать pipeline с уточнениями. Дочерний узел
              появится здесь.
            </Text>
            <Text size="sm" c="dimmed">
              Можно вернуться сюда из любого узла и завести вторую ветку — так
              исследуешь альтернативные пути и сравниваешь итоги.
            </Text>
          </Stack>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ height: "100%", minHeight: 400 }}>
      <Text fw={600} size="sm" mb="xs">
        🌳 Дерево решений ({runs.length} узлов)
      </Text>
      <div
        style={{
          height: "calc(100% - 28px)",
          border: "1px solid var(--mantine-color-gray-3)",
          borderRadius: 8,
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={false}
          panOnDrag
          zoomOnScroll
        >
          <Background gap={16} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  );
}

